import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { filter, first } from 'rxjs';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PictureComponent } from 'src/app/components/picture/picture.component';
import { Company } from 'src/app/models/company';
import { Media } from 'src/app/models/media';
import { User } from 'src/app/models/user';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { MediaService } from 'src/app/services/media.service';
import { ToastService } from 'src/app/services/toast.service';
import { ValidationScopeWarningService } from 'src/app/services/validation-scope-warning.service';
import { AlertUtils } from 'src/app/utils/alert.utils';
import { LoadingUtils } from 'src/app/utils/loading.utils';

@Component({
    selector: 'app-profile-tab-shop',
    templateUrl: './profile-tab-shop.page.html',
    styleUrls: ['./profile-tab-shop.page.scss'],
    standalone: true,
    imports: [CommonModule, TranslatePipe, LoaderComponent, PictureComponent, ImageUploadComponent, ReactiveFormsModule],
})
export class ProfileTabShopPage implements OnInit {
    user: User;
    company: Company;
    loaded = false;
    savingShopName = false;
    cpLoading = false;
    cpProgress = 0;
    cshLoading = false;
    cshProgress: number;

    shopNameForm = new FormGroup({
        shopName: new FormControl('', [Validators.required, Validators.maxLength(30)]),
    });

    constructor(
        private readonly authService: AuthService,
        private readonly companyService: CompanyService,
        private readonly mediaService: MediaService,
        private readonly validationScopeWarningService: ValidationScopeWarningService,
        private readonly toastService: ToastService,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn
            .pipe(
                filter((loggedIn) => !!loggedIn),
                first(),
            )
            .subscribe({
                next: (user) => {
                    this.user = user;
                    this.companyService.getMyCompany(user.id).subscribe({
                        next: (company) => {
                            this.company = company;
                            this.shopNameForm.patchValue({ shopName: company.shopName || '' });
                            if (user.type !== 'pro') {
                                this.shopNameForm.disable();
                            }
                            this.loaded = true;
                        },
                    });
                },
            });
    }

    async saveShopName() {
        if (this.shopNameForm.invalid || !this.company) {
            this.shopNameForm.markAllAsTouched();
            return;
        }

        const confirmed = await this.validationScopeWarningService.confirmIfNeeded();
        if (!confirmed) {
            return;
        }

        this.savingShopName = true;
        await LoadingUtils.show();
        this.companyService.update(this.company.id, { shopName: this.shopNameForm.value.shopName }).subscribe({
            next: (company) => {
                this.company = company;
                this.savingShopName = false;
                LoadingUtils.hide();
            },
            error: (err) => {
                this.savingShopName = false;
                LoadingUtils.hide();
                this.toastService.error(this.getShopNameError(err));
            },
        });
    }

    private getShopNameError(err: any): string {
        const details = err?.error?.details?.shopName;
        const message = Array.isArray(details)
            ? details[0]
            : Array.isArray(details?.shopName)
              ? details.shopName[0]
              : typeof details === 'string'
                ? details
                : err?.error?.error;

        if (typeof message === 'string' && message.length > 0) {
            return message;
        }

        return _('This shop name is already taken.');
    }

    private sendCompanyPicture(media: Media) {
        this.companyService.update(this.company.id, { picture: media.id }).subscribe({
            next: (company) => {
                this.company.picture = company.picture;
                this.cpProgress = 100;
                this.cpLoading = false;
            },
            error: (err) => {
                this.cpLoading = false;
                console.error(err);
            },
        });
    }

    private sendCompanySearchHeader(media: Media) {
        this.companyService.update(this.company.id, { searchHeader: media.id }).subscribe({
            next: (company) => {
                this.company.searchHeader = company.searchHeader;
                this.cshProgress = 100;
                this.cshLoading = false;
            },
            error: (err) => {
                this.cshLoading = false;
                console.error(err);
            },
        });
    }

    async changeCompanyPicture() {
        const confirmed = await this.validationScopeWarningService.confirmIfNeeded();
        if (!confirmed) {
            return;
        }

        this.mediaService
            .pickImage()
            .then((file) => {
                this.cpProgress = 0;
                this.cpLoading = true;
                this.mediaService.upload(
                    file.blob,
                    file.filename,
                    'avatar_company',
                    (progress) => {
                        this.cpProgress = progress;
                    },
                    (media) => {
                        this.sendCompanyPicture(media);
                    },
                    (err) => {
                        if (err === 'upload-error') {
                            this.cpLoading = false;
                            this.cpProgress = 0;
                            AlertUtils.showError(_('An error occured while uploading the picture'));
                        }
                    },
                );
            })
            .catch((err) => {
                if (err === 'upload-error') {
                    this.cpLoading = false;
                    this.cpProgress = 0;
                    AlertUtils.showError(_('An error occured while uploading the picture'));
                }
            });
    }

    async changeCompanySearchHeader() {
        const confirmed = await this.validationScopeWarningService.confirmIfNeeded();
        if (!confirmed) {
            return;
        }

        this.mediaService
            .pickImage()
            .then((file) => {
                this.cshProgress = 0;
                this.cshLoading = true;
                this.mediaService.upload(
                    file.blob,
                    file.filename,
                    'search_header_company',
                    (progress) => {
                        this.cshProgress = progress;
                    },
                    (media) => {
                        this.sendCompanySearchHeader(media);
                    },
                    (err) => {
                        if (err === 'upload-error') {
                            this.cshLoading = false;
                            this.cshProgress = 0;
                            AlertUtils.showError(_('An error occured while uploading the picture'));
                        }
                    },
                );
            })
            .catch((err) => {
                if (err === 'upload-error') {
                    this.cshLoading = false;
                    this.cshProgress = 0;
                    AlertUtils.showError(_('An error occured while uploading the picture'));
                }
            });
    }
}
